import {type HTMLAttributes} from 'svelte/elements';
import {type VariantProps, tv} from 'tailwind-variants';
import {type Class} from 'Catalyst/class';
import {type IconProps} from 'Catalyst/components/Icon';
import {
  sectionNoticeSlotsBaseLayout,
  sectionNoticeSlotsBaseResizingW,
  sectionNoticeSlotsBaseResizingH,
  sectionNoticeSlotsTitleResizingW,
  sectionNoticeSlotsMessageResizingW,
} from 'Catalyst/tokens/output/section-notice';
import {default as SectionNotice} from './SectionNotice.svelte';

export const sectionNoticeTvProps = tv({
  slots: {
    base: `
      ${sectionNoticeSlotsBaseLayout}
      ${sectionNoticeSlotsBaseResizingW}
      ${sectionNoticeSlotsBaseResizingH}
      space-x-[--section-notice-slots-base-item-spacing]
      p-[--section-notice-slots-base-p]
      rounded-[--section-notice-slots-base-border-radius]
    `,
    icon: '',
    title: `
      ${sectionNoticeSlotsTitleResizingW}
      *:font-[--section-notice-slots-title-font-weight]
      [font:--section-notice-slots-title-typography]
    `,
    message: `
      ${sectionNoticeSlotsMessageResizingW}
      *:font-[--section-notice-slots-message-font-weight]
      [font:--section-notice-slots-message-typography]
    `,
  },
  variants: {
    semantic: {
      neutral: {
        base: `
          bg-[--section-notice-variants-semantic-neutral-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-neutral-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-neutral-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-neutral-message-color]
        `,
      },
      info: {
        base: `
          bg-[--section-notice-variants-semantic-info-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-info-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-info-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-info-message-color]
        `,
      },
      error: {
        base: `
          bg-[--section-notice-variants-semantic-error-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-error-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-error-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-error-message-color]
        `,
      },
      warning: {
        base: `
          bg-[--section-notice-variants-semantic-warning-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-warning-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-warning-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-warning-message-color]
        `,
      },
      success: {
        base: `
          bg-[--section-notice-variants-semantic-success-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-success-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-success-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-success-message-color]
        `,
      },
      brand: {
        base: `
          bg-[--section-notice-variants-semantic-brand-base-bg-color]
        `,
        icon: `
          text-[--section-notice-variants-semantic-brand-icon-color]
        `,
        title: `
          text-[--section-notice-variants-semantic-brand-title-color]
        `,
        message: `
          text-[--section-notice-variants-semantic-brand-message-color]
        `,
      },
    },
  },
  defaultVariants: {
    semantic: 'neutral',
  },
});

export type SectionNoticeTVProps = VariantProps<typeof sectionNoticeTvProps>;

export type SectionNoticeOwnProps = Partial<{
  title: string;
  message: string;
  semantic: string;
  icon: IconProps['name'];
}>;

export type SectionNoticeProps = HTMLAttributes<HTMLDivElement> &
  SectionNoticeTVProps &
  SectionNoticeOwnProps &
  Class;

export {SectionNotice};
